import { useState, useEffect, useContext, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { TravelRequest, TravelRequestStatus, ITravelRequest } from '@/lib/request-trip/models/travel-request.model';
import { toast } from '@/hooks/useToast';
import { AuthContext } from '@/lib/auth/models/auth.model';
import { showDevError } from '@/components/ui/dev-error-toast';
import { useI18n } from "@/hooks/useI18n";

export const useTravelRequests = () => {
  const { t } = useI18n();
  const [travelRequests, setTravelRequests] = useState<TravelRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Recupera l'utente dal contesto di autenticazione
  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error('useTravelRequests deve essere usato all\'interno di un AuthProvider');
  }
  const { user } = authContext;

  // Recupera le travel requests assegnate all'utente corrente
  const fetchTravelRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Recupera il ruolo dell'utente
      const userRole = user.user_metadata?.role;

      // Costruisci la query base
      let query = supabase
        .from('travel_requests')
        .select('*')
        .not('status', 'eq', 'rejected');

      // Aggiungi filtro esplicito per Travel Designer
      if (userRole === 'travel_designer' || userRole === 'travel_advisor_member') {
        query = query.eq('assigned_designer_id', user.id);
      }
      // Admin e Traveler vedono tutto (RLS gestisce)

      const { data, error: fetchError } = await query
        .order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Error fetching travel requests:', fetchError);
        setError('Errore nel caricamento delle richieste di viaggio');
        toast({
          title: t('common.error'),
          description: t('travelRequestMessages.loadError'),
          variant: "destructive",
        });
        return;
      }

      const convertedData: TravelRequest[] = data?.map(item =>
        new TravelRequest(item as ITravelRequest)
      ) || [];

      setTravelRequests(convertedData);
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Errore imprevisto nel caricamento delle richieste');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Function to fetch travel request data from Supabase
  const fetchTravelRequest = useCallback(async (requestId: string) => {
    try {
      const { data, error } = await supabase
        .from('travel_requests')
        .select('*')
        .eq('id', requestId)
        .single();

      if (error) {
        console.error('Error fetching travel request:', error);
        showDevError({ error, context: 'fetching travel request' });
        toast({
          title: t('common.error'),
          description: t('travelRequestMessages.loadRequestDataError'),
          variant: "destructive",
        });
        return null;
      }

      return data;
    } catch (error) {
      console.error('Unexpected error fetching travel request:', error);
      return null;
    }
  }, []);

  const fetchTravelRequestsWithUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Recupera le travel requests (filtri RLS già gestiti)
      const { data: requests, error: reqError } = await supabase
        .from('travel_requests')
        .select('*')
        .not('status', 'eq', 'rejected')
        .order('created_at', { ascending: false });

      if (reqError) {
        console.error('Error fetching travel requests:', reqError);
        setError('Errore nel caricamento delle richieste di viaggio');
        toast({
          title: t('common.error'),
          description: t('travelRequestMessages.loadError'),
          variant: "destructive",
        });
        return [];
      }

      const convertedData: TravelRequest[] = requests?.map(item =>
        new TravelRequest(item as ITravelRequest)
      ) || [];

      setTravelRequests(convertedData);

      return convertedData;
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Errore imprevisto nel caricamento delle richieste');
      toast({
        title: t('common.error'),
        description: t('travelRequestMessages.loadUnexpected'),
        variant: "destructive",
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Aggiorna lo status di una travel request
  const updateTravelRequestStatus = async (requestId: string, newStatus: TravelRequestStatus) => {
    try {
      // Aggiorna lo status nella tabella travel_requests
      const { error: updateTravelRequestError } = await supabase
        .from('travel_requests')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId)
        .eq('assigned_designer_id', user.id);

      if (updateTravelRequestError) {
        console.error('Error updating travel request status:', updateTravelRequestError);
        toast({
          title: t('common.error'),
          description: t('travelRequestMessages.updateStatusError'),
          variant: "destructive",
        });
        return false;
      }

      // Aggiorna la lista locale
      setTravelRequests(prev =>
        prev.map(req =>
          req.id === requestId
            ? new TravelRequest({ ...req, status: newStatus, updated_at: new Date().toISOString() })
            : req
        )
      );

      toast({ variant: 'success',
        title: t('common.success'),
        description: t('travelRequestMessages.statusUpdated', { status: newStatus }),
      });

      return true;
    } catch (err) {
      console.error('Unexpected error updating status:', err);
      toast({
        title: t('common.error'),
        description: t('travelRequestMessages.updateStatusUnexpectedError'),
        variant: "destructive",
      });
      return false;
    }
  };

  // Refresh dei dati -> ora usa la versione arricchita
  const refreshData = async () => {
    await fetchTravelRequestsWithUsers();
  };

  // Carica i dati all'inizializzazione: esegue sempre la fetch arricchita
  // useEffect(() => {
  //   let mounted = true;
  //   const load = async () => {
  //     try {
  //       await fetchTravelRequestsWithUsers();
  //     } catch (err) {
  //       if (mounted) {
  //         console.error('Error initializing travel requests:', err);
  //       }
  //     }
  //   };
  //   load();
  //   return () => { mounted = false; };
  // }, [fetchTravelRequestsWithUsers]);

  // Accetta una travel request assegnata
  const acceptTravelRequest = async (requestId: string) => {
    try {
      // Aggiorna lo status della richiesta a 'assigned' (già assegnata)
      const { error: updateError } = await supabase
        .from('travel_requests')
        .update({
          status: 'assigned',
          updated_at: new Date().toISOString(),
          assigned_designer_id: user.id
        })
        .eq('id', requestId);

      if (updateError) {
        console.error('Error accepting travel request:', updateError);
        toast({
          title: t('common.error'),
          description: t('travelRequestMessages.acceptError'),
          variant: "destructive",
        });
        return false;
      }

      // Aggiorna la richiesta nella lista locale
      setTravelRequests(prev =>
        prev.map(req =>
          req.id === requestId
            ? new TravelRequest({ ...req, status: 'assigned', updated_at: new Date().toISOString() })
            : req
        )
      );

      toast({ variant: 'success',
        title: t('common.success'),
        description: t('travelRequestMessages.acceptSuccess'),
      });

      return true;
    } catch (err) {
      console.error('Unexpected error accepting request:', err);
      toast({
        title: t('common.error'),
        description: t('travelRequestMessages.acceptUnexpectedError'),
        variant: "destructive",
      });
      return false;
    }
  };

  // Rifiuta una travel request assegnata
  const declineTravelRequest = async (requestId: string) => {
    try {
      // Rimuovi l'assegnazione impostando assigned_designer_id a NULL e status a pending
      const { error: updateError } = await supabase
        .from('travel_requests')
        .update({
          status: 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId)
        .eq('assigned_designer_id', user.id);

      if (updateError) {
        console.error('Error declining travel request:', updateError);
        toast({
          title: t('common.error'),
          description: t('travelRequestMessages.declineError'),
          variant: "destructive",
        });
        return false;
      }

      setTravelRequests(prev => prev.filter(req => req.id !== requestId));

      toast({ variant: 'success',
          title: t('common.success'),
          description: t('travelRequestMessages.declineSuccess'),
      });

      return true;
    } catch (err) {
      console.error('Unexpected error declining request:', err);
      toast({
          title: t('common.error'),
          description: t('travelRequestMessages.declineUnexpectedError'),
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    travelRequests,
    loading,
    error,
    fetchTravelRequests,
    fetchTravelRequest,
    fetchTravelRequestsWithUsers,
    updateTravelRequestStatus,
    acceptTravelRequest,
    declineTravelRequest,
    refreshData,
  };
};
